import {
  Box,
  Grid,
  GridItem,
  HStack,
  Spinner,
  Switch,
  Text,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { TalentDiscoveryRespUserV3 } from '@revelio/data-access';
import type { AreaData } from '@revelio/replots';

import type { FullProfileInfoData } from '../../CustomDash/components/Chart/FullProfileInfo/types';
import { useCompanyCompareStore } from '../hooks/useCompanyCompareStore';
import type { CompanyRicsIds } from '../hooks/useCompanyRics';
import { useCompetitors } from '../hooks/useCompetitors';
import { useCurrencyFormatter } from '../hooks/useCurrencyFormatter';
import { useCurrencyStore } from '../hooks/useCurrencyStore';
import {
  type OverlayCompany,
  useOverlayDistributions,
} from '../hooks/useOverlayDistributions';
import { usePayDataAvailability } from '../hooks/usePayDataAvailability';
import type {
  GeoLevel,
  ResolvedProfileIds,
  RoleGranularity,
  RoleLevel,
} from '../hooks/useResolveProfileIds';
import {
  type CompanyEntity,
  useSalaryDistribution,
} from '../hooks/useSalaryDistribution';
import { useValidateCompanies } from '../hooks/useValidateCompanies';
import { useWorkHistoryCompanies } from '../hooks/useWorkHistoryCompanies';
import type { SalaryToggles } from '../IndividualPotentialPage';
import { SectionCard } from '../SectionCard';
import { convertDistribution, convertToAreaData } from '../salary/chart-utils';
import { GranularitySelector } from '../salary/GranularitySelector';
import {
  type ChipCompany,
  COMPARE_OVERLAY_COLORS,
  CompanyComparisonChips,
  MAX_OVERLAY_ENTITIES,
  OVERLAY_COLORS,
  PRIMARY_COLOR,
  SelectedComparisonsChips,
} from '../salary/OverlayChips';
import { SalaryChartContent } from '../salary/SalaryChartContent';
import { StatBox } from '../salary/StatBox';

const GEO_TYPE_LABELS: Record<string, string> = {
  city: 'City',
  msa: 'Metro Area',
  country: 'Country',
  region: 'Region',
};

const ROLE_GRANULARITY_LABELS: Record<RoleGranularity, string> = {
  role_k15000: 'Exact Title',
  role_k1500: 'Narrow Role',
  role_k150: 'Role',
  role_k7: 'Broad Category',
};

type CompanyGranularity = 'company' | 'rics_k10' | 'rics_k50' | 'rics_k400';

const COMPANY_GRANULARITY_LABELS: Record<CompanyGranularity, string> = {
  company: 'Company',
  rics_k400: 'Industry (Narrow)',
  rics_k50: 'Industry (Mid)',
  rics_k10: 'Industry (Broad)',
};

interface SalarySectionProps {
  profileData: FullProfileInfoData;
  resolvedIds: ResolvedProfileIds;
  rics: CompanyRicsIds;
  isResolving: boolean;
  salaryToggles: SalaryToggles;
  onToggleChange: (key: keyof SalaryToggles) => void;
  userSalary: number;
  salaryTypeLabel: 'Base' | 'TC';
  isSimulatedSalary: boolean;
  selectedUser: TalentDiscoveryRespUserV3;
}

export const SalarySection = ({
  resolvedIds,
  rics,
  isResolving,
  salaryToggles,
  onToggleChange,
  userSalary,
  salaryTypeLabel,
  selectedUser,
}: SalarySectionProps) => {
  const salaryFormatter = useCurrencyFormatter();
  const currencyCode = useCurrencyStore((s) => s.selected?.code ?? 'USD');
  const exchangeRate = useCurrencyStore((s) => s.selected?.rate ?? 1);

  // Local state for role granularity selection
  const [selectedRoleIndex, setSelectedRoleIndex] = useState(0);
  const selectedRole = useMemo((): RoleLevel | null => {
    if (resolvedIds.roleLevels.length === 0) return null;
    return (
      resolvedIds.roleLevels[selectedRoleIndex] ??
      resolvedIds.roleLevels[0] ??
      null
    );
  }, [resolvedIds.roleLevels, selectedRoleIndex]);

  // Local state for geo granularity selection
  const [selectedGeoIndex, setSelectedGeoIndex] = useState(0);
  const selectedGeo = useMemo(() => {
    if (resolvedIds.geoLevels.length === 0) return null;
    return (
      resolvedIds.geoLevels[selectedGeoIndex] ??
      resolvedIds.geoLevels[0] ??
      null
    );
  }, [resolvedIds.geoLevels, selectedGeoIndex]);

  // Local state for company granularity
  const [companyGranularity, setCompanyGranularity] =
    useState<CompanyGranularity>('company');

  const availableCompanyGranularities = useMemo(() => {
    const options: CompanyGranularity[] = [];
    if (resolvedIds.companyRcid) options.push('company');
    if (rics.ricsK400) options.push('rics_k400');
    if (rics.ricsK50) options.push('rics_k50');
    if (rics.ricsK10) options.push('rics_k10');
    return options;
  }, [resolvedIds.companyRcid, rics]);

  const selectedCompanyEntity: CompanyEntity | null = useMemo(() => {
    if (companyGranularity === 'company' && resolvedIds.companyRcid) {
      return { type: 'company', id: resolvedIds.companyRcid };
    }
    if (companyGranularity === 'rics_k400' && rics.ricsK400) {
      return { type: 'rics_k400', id: rics.ricsK400 };
    }
    if (companyGranularity === 'rics_k50' && rics.ricsK50) {
      return { type: 'rics_k50', id: rics.ricsK50 };
    }
    if (companyGranularity === 'rics_k10' && rics.ricsK10) {
      return { type: 'rics_k10', id: rics.ricsK10 };
    }
    if (resolvedIds.companyRcid) {
      return { type: 'company', id: resolvedIds.companyRcid };
    }
    return null;
  }, [companyGranularity, resolvedIds.companyRcid, rics]);

  // --- Overlay comparison companies ---
  const { companies: workHistoryCompanies } =
    useWorkHistoryCompanies(selectedUser);

  const salaryCompetitorFallback = useMemo(() => {
    if (!rics.ricsK10 && !rics.ricsK50 && !rics.ricsK400) return null;
    return { rics };
  }, [rics]);

  const { competitors, isLoading: competitorsLoading } = useCompetitors(
    resolvedIds.companyRcid,
    15,
    salaryCompetitorFallback
  );
  const { validatedCompanies, isValidating } = useValidateCompanies(
    workHistoryCompanies,
    resolvedIds.companyRcid
  );

  const candidateRcids = useMemo(() => {
    const seen = new Set<number>();
    const rcids: number[] = [];
    if (resolvedIds.companyRcid) seen.add(resolvedIds.companyRcid);
    for (const vc of validatedCompanies) {
      if (vc.status === 'valid' && !seen.has(vc.rcid)) {
        seen.add(vc.rcid);
        rcids.push(vc.rcid);
      }
    }
    for (const comp of competitors) {
      if (!seen.has(comp.id)) {
        seen.add(comp.id);
        rcids.push(comp.id);
      }
    }
    return rcids;
  }, [validatedCompanies, competitors, resolvedIds.companyRcid]);

  const { payDataStatus, isChecking: isCheckingPayData } =
    usePayDataAvailability(candidateRcids, resolvedIds, salaryToggles);

  const [enabledRcids, setEnabledRcids] = useState<Set<number>>(new Set());

  const handleToggleChip = useCallback((rcid: number) => {
    setEnabledRcids((prev) => {
      const next = new Set(prev);
      if (next.has(rcid)) {
        next.delete(rcid);
      } else {
        if (next.size >= MAX_OVERLAY_ENTITIES) return prev;
        next.add(rcid);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!salaryToggles.company) {
      setEnabledRcids(new Set());
    }
  }, [salaryToggles.company]);

  useEffect(() => {
    if (payDataStatus.size === 0) return;
    setEnabledRcids((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const rcid of prev) {
        if (payDataStatus.get(rcid) === 'no_data') {
          next.delete(rcid);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [payDataStatus]);

  const chips = useMemo((): ChipCompany[] => {
    const seen = new Set<number>();
    const result: ChipCompany[] = [];
    let colorIdx = 0;

    if (resolvedIds.companyRcid) seen.add(resolvedIds.companyRcid);

    for (const vc of validatedCompanies) {
      if (seen.has(vc.rcid)) continue;
      seen.add(vc.rcid);
      result.push({
        rcid: vc.rcid,
        name: vc.mappedName ?? vc.companyName,
        source: 'work_history',
        enabled: enabledRcids.has(vc.rcid),
        validationStatus: vc.status,
        payDataStatus: payDataStatus.get(vc.rcid) ?? 'checking',
        color:
          OVERLAY_COLORS[colorIdx % OVERLAY_COLORS.length] ?? PRIMARY_COLOR,
      });
      colorIdx++;
    }

    for (const comp of competitors) {
      if (seen.has(comp.id)) continue;
      seen.add(comp.id);
      result.push({
        rcid: comp.id,
        name: comp.shortName,
        source: 'competitor',
        enabled: enabledRcids.has(comp.id),
        validationStatus: 'valid',
        payDataStatus: payDataStatus.get(comp.id) ?? 'checking',
        color:
          OVERLAY_COLORS[colorIdx % OVERLAY_COLORS.length] ?? PRIMARY_COLOR,
      });
      colorIdx++;
    }

    return result;
  }, [
    validatedCompanies,
    competitors,
    resolvedIds.companyRcid,
    enabledRcids,
    payDataStatus,
  ]);

  // --- Global compare store integration ---
  const globalSelectedCompanies = useCompanyCompareStore(
    (s) => s.selectedCompanies
  );

  const [enabledCompareRcids, setEnabledCompareRcids] = useState<Set<number>>(
    new Set()
  );

  useEffect(() => {
    const globalRcids = new Set(globalSelectedCompanies.map((c) => c.rcid));
    setEnabledCompareRcids((prev) => {
      const next = new Set<number>();
      for (const rcid of prev) {
        if (globalRcids.has(rcid)) next.add(rcid);
      }
      for (const rcid of globalRcids) {
        next.add(rcid);
      }
      return next;
    });
  }, [globalSelectedCompanies]);

  useEffect(() => {
    if (!salaryToggles.company) {
      setEnabledCompareRcids(new Set());
    }
  }, [salaryToggles.company]);

  const handleToggleCompareChip = useCallback((rcid: number) => {
    setEnabledCompareRcids((prev) => {
      const next = new Set(prev);
      if (next.has(rcid)) {
        next.delete(rcid);
      } else {
        next.add(rcid);
      }
      return next;
    });
  }, []);

  const localChipRcids = useMemo(
    () => new Set(chips.map((c) => c.rcid)),
    [chips]
  );

  const activeOverlays = useMemo((): OverlayCompany[] => {
    const overlays: OverlayCompany[] = chips
      .filter(
        (c) =>
          c.enabled &&
          c.validationStatus === 'valid' &&
          c.payDataStatus !== 'no_data'
      )
      .map((c) => ({ rcid: c.rcid, name: c.name, source: c.source }));

    for (const company of globalSelectedCompanies) {
      if (
        enabledCompareRcids.has(company.rcid) &&
        !localChipRcids.has(company.rcid)
      ) {
        if (company.rcid === resolvedIds.companyRcid) continue;
        overlays.push({
          rcid: company.rcid,
          name: company.name,
          source: 'competitor',
        });
      }
    }

    return overlays;
  }, [
    chips,
    globalSelectedCompanies,
    enabledCompareRcids,
    localChipRcids,
    resolvedIds.companyRcid,
  ]);

  const enabledCount = activeOverlays.length;

  const {
    overlayDistributions,
    primaryOverride,
    isLoading: overlaysLoading,
  } = useOverlayDistributions(
    activeOverlays,
    resolvedIds,
    salaryToggles,
    selectedGeo,
    selectedRole
  );

  const {
    distribution,
    label,
    percentile,
    median,
    p10,
    p90,
    isLoading,
    error,
  } = useSalaryDistribution(
    resolvedIds,
    salaryToggles,
    userSalary,
    selectedGeo,
    selectedCompanyEntity,
    selectedRole
  );

  const convertedDistribution = useMemo(
    () => convertDistribution(distribution, exchangeRate),
    [distribution, exchangeRate]
  );
  const convertedUserSalary = userSalary * exchangeRate;

  const primaryChartLabel = useMemo(() => {
    if (primaryOverride && resolvedIds.companyName) {
      return resolvedIds.companyName;
    }
    return label || 'Salary Distribution';
  }, [primaryOverride, resolvedIds.companyName, label]);

  const areaData = useMemo(() => {
    if (primaryOverride && overlayDistributions.length > 0) {
      const primaryConverted = convertDistribution(
        primaryOverride.distribution,
        exchangeRate
      );

      const primarySeries: AreaData = {
        label: primaryChartLabel,
        values: primaryConverted
          .filter(
            (d) =>
              typeof d.value === 'number' &&
              typeof d.kde === 'number' &&
              // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
              !isNaN(d.value) &&
              // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
              !isNaN(d.kde)
          )
          .map((d) => ({ x: d.value, value: d.kde, secondaryValue: d.cdf })),
      };

      const overlaySeries: AreaData[] = overlayDistributions.map((overlay) => {
        const converted = convertDistribution(
          overlay.distribution,
          exchangeRate
        );
        return {
          label: overlay.companyName,
          values: converted
            .filter(
              (d) =>
                typeof d.value === 'number' &&
                typeof d.kde === 'number' &&
                // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
                !isNaN(d.value) &&
                // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
                !isNaN(d.kde)
            )
            .map((d) => ({ x: d.value, value: d.kde, secondaryValue: d.cdf })),
        };
      });

      return [primarySeries, ...overlaySeries];
    }

    return convertToAreaData(convertedDistribution, primaryChartLabel);
  }, [
    convertedDistribution,
    primaryChartLabel,
    primaryOverride,
    overlayDistributions,
    exchangeRate,
  ]);

  const chartColors = useMemo((): string[] => {
    const enabledChips = chips.filter(
      (c) => c.enabled && c.validationStatus === 'valid'
    );
    const colors = [PRIMARY_COLOR, ...enabledChips.map((c) => c.color)];

    let compareIdx = 0;
    for (const company of globalSelectedCompanies) {
      if (
        enabledCompareRcids.has(company.rcid) &&
        !localChipRcids.has(company.rcid)
      ) {
        if (company.rcid === resolvedIds.companyRcid) continue;
        colors.push(
          COMPARE_OVERLAY_COLORS[compareIdx % COMPARE_OVERLAY_COLORS.length] ??
            '#9F7AEA'
        );
        compareIdx++;
      }
    }

    return colors;
  }, [
    chips,
    globalSelectedCompanies,
    enabledCompareRcids,
    localChipRcids,
    resolvedIds.companyRcid,
  ]);

  const handleRoleSelect = (role: RoleLevel) => {
    const idx = resolvedIds.roleLevels.findIndex((r) => r.type === role.type);
    if (idx >= 0) setSelectedRoleIndex(idx);
  };

  const activeFilterDescription = useMemo(() => {
    const parts: string[] = [];
    if (salaryToggles.role && selectedRole) {
      parts.push(selectedRole.name);
    }
    if (salaryToggles.seniority && resolvedIds.seniorityId) {
      parts.push('matching seniority');
    }
    if (salaryToggles.geo && selectedGeo) {
      parts.push(`in ${selectedGeo.name}`);
    }
    if (salaryToggles.company && selectedCompanyEntity) {
      if (selectedCompanyEntity.type === 'company') {
        parts.push(`at ${resolvedIds.companyName}`);
      } else {
        const industryLabel =
          rics.ricsK400Name ?? COMPANY_GRANULARITY_LABELS[companyGranularity];
        parts.push(`in ${industryLabel}`);
      }
    }
    return parts.length > 0
      ? `Showing salary distribution for ${parts.join(', ')}`
      : 'Showing worldwide salary distribution';
  }, [
    salaryToggles,
    resolvedIds,
    selectedRole,
    selectedGeo,
    selectedCompanyEntity,
    rics,
    companyGranularity,
  ]);

  const handleGeoSelect = (geo: GeoLevel) => {
    const idx = resolvedIds.geoLevels.findIndex((g) => g.type === geo.type);
    if (idx >= 0) setSelectedGeoIndex(idx);
  };

  const companyLabel = useMemo(() => {
    if (companyGranularity === 'company') {
      return resolvedIds.companyName
        ? `Company (${resolvedIds.companyName})`
        : 'Company';
    }
    const granLabel = COMPANY_GRANULARITY_LABELS[companyGranularity];
    if (rics.ricsK400Name && companyGranularity === 'rics_k400') {
      return `${granLabel} (${rics.ricsK400Name})`;
    }
    return granLabel;
  }, [companyGranularity, resolvedIds.companyName, rics.ricsK400Name]);

  const geoLabel = useMemo(() => {
    if (!selectedGeo) return 'Geography';
    const typeLabel = GEO_TYPE_LABELS[selectedGeo.type] ?? selectedGeo.type;
    return `${typeLabel}: ${selectedGeo.name}`;
  }, [selectedGeo]);

  const roleLabel = useMemo(() => {
    if (!selectedRole) return 'Role';
    const granLabel = ROLE_GRANULARITY_LABELS[selectedRole.type];
    return `${granLabel}: ${selectedRole.name}`;
  }, [selectedRole]);

  const toggleItems: Array<{
    key: keyof SalaryToggles;
    label: string;
    resolved: boolean;
    extra?: React.ReactNode;
  }> = [
    {
      key: 'role',
      label: roleLabel,
      resolved: resolvedIds.roleId !== null,
      extra: salaryToggles.role ? (
        <GranularitySelector
          options={resolvedIds.roleLevels}
          selected={selectedRole as RoleLevel}
          onSelect={handleRoleSelect}
          getKey={(r) => r.type}
          getLabel={(r) => `${ROLE_GRANULARITY_LABELS[r.type]}: ${r.name}`}
          isSelected={(item, sel) => item.type === sel.type}
          minWidth="200px"
        />
      ) : null,
    },
    {
      key: 'seniority',
      label: 'Seniority',
      resolved: resolvedIds.seniorityId !== null,
    },
    {
      key: 'geo',
      label: geoLabel,
      resolved: resolvedIds.geoLevels.length > 0,
      extra: salaryToggles.geo ? (
        <GranularitySelector
          options={resolvedIds.geoLevels}
          selected={selectedGeo as GeoLevel}
          onSelect={handleGeoSelect}
          getKey={(g) => g.type}
          getLabel={(g) => `${GEO_TYPE_LABELS[g.type] ?? g.type}: ${g.name}`}
          isSelected={(item, sel) => item.type === sel.type}
        />
      ) : null,
    },
    {
      key: 'company',
      label: companyLabel,
      resolved: resolvedIds.companyRcid !== null || rics.ricsK10 !== null,
      extra:
        salaryToggles.company && availableCompanyGranularities.length > 1 ? (
          <GranularitySelector
            options={availableCompanyGranularities}
            selected={companyGranularity}
            onSelect={setCompanyGranularity}
            getKey={(g) => g}
            getLabel={(g) => COMPANY_GRANULARITY_LABELS[g]}
            isSelected={(item, sel) => item === sel}
            minWidth="180px"
          />
        ) : null,
    },
  ];

  const heading =
    salaryTypeLabel === 'Base'
      ? 'Base Salary Intelligence'
      : 'Total Compensation Intelligence';

  return (
    <SectionCard heading={heading}>
      <HStack spacing={6} flexWrap="wrap">
        {toggleItems.map((item) => (
          <HStack key={item.key} spacing={2}>
            <Switch
              size="sm"
              isChecked={salaryToggles[item.key]}
              onChange={() => onToggleChange(item.key)}
              isDisabled={!item.resolved}
              colorScheme="blue"
            />
            <Text fontSize="sm" color={item.resolved ? 'gray.700' : 'gray.400'}>
              {item.label}
            </Text>
            {item.extra}
          </HStack>
        ))}
      </HStack>

      <CompanyComparisonChips
        chips={chips}
        onToggle={handleToggleChip}
        isValidating={isValidating}
        competitorsLoading={competitorsLoading}
        isCheckingPayData={isCheckingPayData}
        enabledCount={enabledCount}
        companyToggleOn={salaryToggles.company}
      />

      <SelectedComparisonsChips
        enabledCompareRcids={enabledCompareRcids}
        onToggle={handleToggleCompareChip}
        enabledCount={enabledCount}
        maxOverlays={MAX_OVERLAY_ENTITIES}
        companyToggleOn={salaryToggles.company}
      />

      <Text fontSize="xs" color="gray.500">
        {isResolving ? 'Resolving filters...' : activeFilterDescription}
      </Text>

      <SalaryChartContent
        isLoading={isLoading || isResolving}
        error={error}
        areaData={areaData}
        userSalary={convertedUserSalary}
        rawUserSalary={userSalary}
        percentile={percentile}
        salaryFormatter={salaryFormatter}
        currencyCode={currencyCode}
        salaryTypeLabel={salaryTypeLabel}
        chartColors={chartColors}
      />

      {enabledCount > 0 && (
        <HStack spacing={4} flexWrap="wrap">
          <HStack spacing={1}>
            <Box w="12px" h="3px" bg={PRIMARY_COLOR} borderRadius="full" />
            <Text fontSize="xs" color="gray.600">
              {primaryChartLabel}
            </Text>
          </HStack>
          {chips
            .filter((c) => c.enabled && c.validationStatus === 'valid')
            .map((chip) => (
              <HStack key={chip.rcid} spacing={1}>
                <Box w="12px" h="3px" bg={chip.color} borderRadius="full" />
                <Text fontSize="xs" color="gray.600">
                  {chip.name}
                </Text>
              </HStack>
            ))}
          {globalSelectedCompanies
            .filter(
              (c) =>
                enabledCompareRcids.has(c.rcid) &&
                !localChipRcids.has(c.rcid) &&
                c.rcid !== resolvedIds.companyRcid
            )
            .map((company, idx) => (
              <HStack key={`compare-${company.rcid}`} spacing={1}>
                <Box
                  w="12px"
                  h="3px"
                  bg={
                    COMPARE_OVERLAY_COLORS[
                      idx % COMPARE_OVERLAY_COLORS.length
                    ] ?? '#9F7AEA'
                  }
                  borderRadius="full"
                />
                <Text fontSize="xs" color="gray.600">
                  {company.name}
                </Text>
              </HStack>
            ))}
          {overlaysLoading && <Spinner size="xs" color="blue.400" />}
        </HStack>
      )}

      {!isLoading && !error && distribution.length > 0 && (
        <Grid templateColumns="repeat(3, 1fr)" gap={3}>
          <GridItem>
            <StatBox
              label={`Median ${salaryTypeLabel}`}
              value={median !== null ? salaryFormatter(median) : null}
            />
          </GridItem>
          <GridItem>
            <StatBox
              label="P10 (Low End)"
              value={p10 !== null ? salaryFormatter(p10) : null}
            />
          </GridItem>
          <GridItem>
            <StatBox
              label="P90 (High End)"
              value={p90 !== null ? salaryFormatter(p90) : null}
            />
          </GridItem>
        </Grid>
      )}
    </SectionCard>
  );
};
